import { createStore, createEvent, sample } from 'effector';
import { isEqual } from 'lodash';
import { signout, $isAuthenticated } from '@features/common';
import { FormInstance } from '@unicorn/effector-form';

type CreateFilterBagOptions = {
  resetFormOnSignout?: boolean;
  submitFormOnReset?: boolean;
};

/**
 * Фабрика фильтров
 * @param defaultFilters - начальные значения фильтров
 * @param form - форма фильтров
 * @param options - настройки обработки сброса формы
 * @returns Объект со сторами и событиями
 */

export const createFilterBag = <T>(
  defaultFilters: T,
  form: FormInstance<T>,
  options: CreateFilterBagOptions = {}
) => {
  const { resetFormOnSignout = true, submitFormOnReset = true } = options;

  const changedFilterVisibility = createEvent<boolean>();

  const $isFilterOpen = createStore<boolean>(false);
  const $isFiltersApply = createStore<boolean>(false);
  const $isDefaultFilterValues = createStore<boolean>(true);

  $isFilterOpen.on(changedFilterVisibility, (_, visibility) => visibility).reset(signout);
  $isFiltersApply.reset(signout);
  $isDefaultFilterValues.reset(signout);

  if (form) {
    /** После самбита формы, принять текущее состояние формы за исходное  */
    sample({
      clock: form.submitted,
      target: form.initValues,
    });

    if (submitFormOnReset) {
      /** При сбросе данные запрашиваются без фильтров */
      sample({
        clock: form.reset,
        source: $isAuthenticated,
        filter: (isAuthenticated) => Boolean(isAuthenticated),
        fn: () => ({ ...defaultFilters }),
        target: [form.updateValues, form.submit],
      });
    }

    /** Установка активности фильтров */
    sample({
      clock: form.submitted,
      fn: (values) => !isEqual(values, defaultFilters),
      target: $isFiltersApply,
    });

    /** Проверка соответствия фильтров дефолтным значениям */
    sample({
      clock: form.values,
      fn: (values) => isEqual(values, defaultFilters),
      target: $isDefaultFilterValues,
    });

    if (resetFormOnSignout) {
      /** После разлогинивания сбрасывается форма */
      sample({
        clock: signout,
        target: form.reset,
      });
    }
  }

  return {
    changedFilterVisibility,
    $isFilterOpen,
    $isFiltersApply,
    $isDefaultFilterValues,
  };
};
